import { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  useAdminAnalytics,
  useAdminAssessmentTrend,
  useAdminRangeStats,
  type AllSchoolRow,
  type CapProximityRow,
} from '@/hooks/useAdminAnalytics';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { AlertTriangle, CheckCircle2, Download, X } from 'lucide-react';
import { buildCsv, downloadCsv, isoDate, todayStamp } from '@/lib/exportCsv';
import AnalyticsAdvancedFilters, {
  type FilterGroup,
  applyAdvancedGroups,
} from '@/components/admin/AnalyticsAdvancedFilters';

const GRADE_COLOURS: Record<string, string> = {
  WTS: 'hsl(38, 92%, 50%)',
  ES:  'hsl(142, 71%, 45%)',
  GD:  'hsl(221, 83%, 53%)',
};

const BILLING_STATUS_COLOURS: Record<string, string> = {
  active:    'hsl(142, 71%, 45%)',
  trialing:  'hsl(221, 83%, 53%)',
  past_due:  'hsl(0, 84%, 60%)',
  cancelled: 'hsl(0, 84%, 60%)',
  paused:    'hsl(38, 92%, 50%)',
  unknown:   'hsl(215, 16%, 47%)',
};

const USAGE_BAND_ORDER = ['0', '1-10', '11-50', '51-200', '200+'];

function getMonthlyCapForSchool(planTier: string | null, planSubTier: string | null): number | null {
  if (planSubTier === 'starter')  return 200;
  if (planSubTier === 'standard') return 500;
  if (planSubTier === 'plus')     return 1000;
  if (planTier === 'free')        return 10;
  if (planTier === 'individual')  return 50;
  return null;
}

function StatCard({ label, value, sub }: { label: string; value: string | number | null; sub?: string }) {
  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <p className="text-2xl font-bold">{value ?? '—'}</p>
        <p className="text-sm text-muted-foreground mt-0.5">{label}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function StatCardSkeleton() {
  return (
    <Card>
      <CardContent className="pt-5 pb-4 space-y-2">
        <Skeleton className="h-7 w-16" />
        <Skeleton className="h-4 w-32" />
      </CardContent>
    </Card>
  );
}

function pct(n: number, total: number) {
  if (!total) return '—';
  return `${Math.round((n / total) * 100)}%`;
}

function formatDate(iso: string | null) {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function planLabel(tier: string | null, sub: string | null) {
  if (!tier) return '—';
  if (sub && sub !== 'none') return `${tier} / ${sub}`;
  return tier;
}

function capPct(row: CapProximityRow) {
  return Math.round((row.assessments_this_month / row.monthly_cap) * 100);
}

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const { data, isLoading } = useAdminAnalytics();
  const { data: trend = [], isLoading: trendLoading } = useAdminAssessmentTrend();

  // ── Filters ──────────────────────────────────────────────────────────────
  const [filterPlan, setFilterPlan]     = useState('all');
  const [filterBilling, setFilterBilling] = useState('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'suspended'>('all');
  const [filterSchool, setFilterSchool] = useState('');
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');

  const { data: rangeStats, isLoading: rangeLoading } = useAdminRangeStats(
    dateFrom || null,
    dateTo   || null,
  );

  const hasFilters =
    filterPlan !== 'all' ||
    filterBilling !== 'all' ||
    filterStatus !== 'all' ||
    !!filterSchool.trim() ||
    filterGroups.length > 0 ||
    !!dateFrom ||
    !!dateTo;

  function clearFilters() {
    setFilterPlan('all');
    setFilterBilling('all');
    setFilterStatus('all');
    setFilterSchool('');
    setFilterGroups([]);
    setDateFrom('');
    setDateTo('');
  }

  // ── Derived: filtered school list ────────────────────────────────────────
  const filteredSchools = useMemo<AllSchoolRow[]>(() => {
    let s = data?.all_schools ?? [];
    if (filterPlan !== 'all')        s = s.filter(x => x.plan_tier === filterPlan);
    if (filterBilling !== 'all')     s = s.filter(x => x.billing_status === filterBilling);
    if (filterStatus === 'active')   s = s.filter(x => !x.suspended_at);
    if (filterStatus === 'suspended') s = s.filter(x => !!x.suspended_at);
    if (filterSchool.trim()) {
      const q = filterSchool.trim().toLowerCase();
      s = s.filter(x => x.school_name.toLowerCase().includes(q));
    }
    if (dateFrom) s = s.filter(x => x.school_created_at && x.school_created_at >= dateFrom);
    if (dateTo)   s = s.filter(x => x.school_created_at && x.school_created_at.substring(0, 10) <= dateTo);
    s = applyAdvancedGroups(s, filterGroups);
    return s;
  }, [data, filterPlan, filterBilling, filterStatus, filterSchool, dateFrom, dateTo, filterGroups]);

  const filteredTotal = filteredSchools.length;
  const totalSchools  = data?.all_schools?.length ?? data?.overview?.total_schools ?? 0;

  // Derived chart data
  const filteredPlanChartData = useMemo(() => {
    const map: Record<string, number> = {};
    for (const s of filteredSchools) {
      const key = s.plan_tier ?? 'unknown';
      map[key] = (map[key] ?? 0) + 1;
    }
    return Object.entries(map).map(([name, count]) => ({ name, count }));
  }, [filteredSchools]);

  const filteredBillingStatusData = useMemo(() => {
    const map: Record<string, number> = {};
    for (const s of filteredSchools) {
      const key = s.billing_status ?? 'unknown';
      map[key] = (map[key] ?? 0) + 1;
    }
    return Object.entries(map).map(([billing_status, cnt]) => ({ billing_status, cnt }));
  }, [filteredSchools]);

  const filteredUsageBands = useMemo(() => {
    const bandFor = (n: number) =>
      n === 0 ? '0' : n <= 10 ? '1-10' : n <= 50 ? '11-50' : n <= 200 ? '51-200' : '200+';
    const map: Record<string, number> = {};
    for (const s of filteredSchools) {
      const band = bandFor(s.total_assessments);
      map[band] = (map[band] ?? 0) + 1;
    }
    return USAGE_BAND_ORDER.map(band => ({ band, cnt: map[band] ?? 0 }));
  }, [filteredSchools]);

  const filteredTopSchools = useMemo(() =>
    filteredSchools.slice().sort((a, b) => b.total_assessments - a.total_assessments).slice(0, 10),
    [filteredSchools],
  );

  const filteredUnderusedSchools = useMemo(() =>
    filteredSchools
      .filter(s => ['active', 'trialing'].includes(s.billing_status ?? '') && s.assessments_last_30d === 0 && !s.suspended_at)
      .sort((a, b) => a.school_name.localeCompare(b.school_name)),
    [filteredSchools],
  );

  const filteredCapProximity = useMemo<CapProximityRow[]>(() =>
    filteredSchools
      .map(s => {
        const cap = getMonthlyCapForSchool(s.plan_tier, s.plan_sub_tier);
        if (!cap) return null;
        if (s.assessments_this_month / cap < 0.8) return null;
        return {
          school_id: s.school_id,
          school_name: s.school_name,
          plan_tier: s.plan_tier,
          plan_sub_tier: s.plan_sub_tier,
          billing_status: s.billing_status,
          assessments_this_month: s.assessments_this_month,
          monthly_cap: cap,
        };
      })
      .filter(Boolean) as CapProximityRow[],
    [filteredSchools],
  );

  const [schoolPage, setSchoolPage] = useState(0);
  const PAGE_SIZE = 25;

  // Reset to page 0 whenever the filtered set changes
  const prevFilterKey = useRef('');
  const filterKey = `${filterPlan}|${filterBilling}|${filterStatus}|${filterSchool}|${dateFrom}|${dateTo}|${filterGroups.length}`;
  if (filterKey !== prevFilterKey.current) {
    prevFilterKey.current = filterKey;
    if (schoolPage !== 0) setSchoolPage(0);
  }

  const pagedSchools = filteredSchools.slice(schoolPage * PAGE_SIZE, (schoolPage + 1) * PAGE_SIZE);
  const totalPages   = Math.ceil(filteredSchools.length / PAGE_SIZE);

  const overview = data?.overview;

  const filteredOverview = useMemo(() => {
    if (!hasFilters) return null; // use raw RPC data when nothing is filtered
    const monthStart = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1)).toISOString();
    return {
      total_schools:             filteredSchools.length,
      active_schools:            filteredSchools.filter(s => s.billing_status === 'active' && !s.suspended_at).length,
      suspended_schools:         filteredSchools.filter(s => !!s.suspended_at).length,
      new_schools_this_month:    filteredSchools.filter(s => !!s.school_created_at && s.school_created_at >= monthStart).length,
      total_assessments:         filteredSchools.reduce((n, s) => n + s.total_assessments, 0),
      assessments_this_month:    filteredSchools.reduce((n, s) => n + s.assessments_this_month, 0),
    };
  }, [hasFilters, filteredSchools]);

  // Grade distribution for pie chart
  const gradeData = (data?.grade_distribution ?? [])
    .filter(g => g.grade !== 'unknown')
    .map(g => ({ name: g.grade.toUpperCase(), value: g.cnt, fill: GRADE_COLOURS[g.grade.toUpperCase()] ?? '#94a3b8' }));

  // ── CSV exports ──────────────────────────────────────────────────────────
  function exportSummary() {
    const ov = overview;
    const ad = data?.adoption;
    const inv = data?.invite_stats;
    const fa = data?.fluency_averages;
    const stamp = todayStamp();
    const csv = buildCsv(
      ['Metric', 'Value'],
      [
        ['Export date', stamp],
        ['--- Platform overview ---', ''],
        ['Total schools', ov?.total_schools ?? ''],
        ['Active schools', ov?.active_schools ?? ''],
        ['Suspended schools', ov?.suspended_schools ?? ''],
        ['New schools this month', ov?.new_schools_this_month ?? ''],
        ['Total users', ov?.total_users ?? ''],
        ['New users this month', ov?.new_users_this_month ?? ''],
        ['Total assessments', ov?.total_assessments ?? ''],
        ['Assessments this month', ov?.assessments_this_month ?? ''],
        ['Avg assessments / active school (this month)', ov?.avg_assessments_per_active_school ?? ''],
        ['--- Product adoption ---', ''],
        ['Schools with pupils added', ad?.schools_with_pupils ?? ''],
        ['Schools with classes set up', ad?.schools_with_classes ?? ''],
        ['Schools with a completed assessment', ad?.schools_with_completed_assessment ?? ''],
        ['Monthly active schools', ad?.monthly_active_schools ?? ''],
        ['Teacher invite acceptance rate', inv ? `${inv.accepted_invites}/${inv.total_invites}` : ''],
        ['--- Fluency averages ---', ''],
        ['Avg syntactic maturity (/10)', fa?.avg_syntactic_maturity ?? ''],
        ['Avg textual cohesion (/10)', fa?.avg_textual_cohesion ?? ''],
        ['Avg lexical precision (/10)', fa?.avg_lexical_precision ?? ''],
        ['Fluency sample count', fa?.fluency_sample_count ?? ''],
      ],
    );
    downloadCsv(`writeassess-analytics-summary-${stamp}.csv`, csv);
  }

  function exportSchools() {
    const schools = hasFilters ? filteredSchools : (data?.all_schools ?? []);
    const stamp = todayStamp();
    const suffix = hasFilters ? '-filtered' : '';
    const csv = buildCsv(
      ['School name', 'Plan tier', 'Plan sub-tier', 'Billing status', 'Suspended', 'Total assessments', 'This month', 'Last 30 days', 'Last assessment'],
      schools.map(s => [
        s.school_name,
        s.plan_tier ?? '',
        s.plan_sub_tier && s.plan_sub_tier !== 'none' ? s.plan_sub_tier : '',
        s.billing_status ?? '',
        s.suspended_at ? isoDate(s.suspended_at) : 'No',
        s.total_assessments,
        s.assessments_this_month,
        s.assessments_last_30d,
        s.last_assessment_at ? isoDate(s.last_assessment_at) : 'Never',
      ]),
    );
    downloadCsv(`writeassess-schools${suffix}-${stamp}.csv`, csv);
  }

  function exportNearLimit() {
    const stamp = todayStamp();
    const rows = filteredCapProximity.slice().sort((a, b) => capPct(b) - capPct(a));
    const csv = buildCsv(
      ['School name', 'Plan', 'Billing status', 'Assessments this month', 'Monthly cap', '% used'],
      rows.map(r => [
        r.school_name,
        planLabel(r.plan_tier, r.plan_sub_tier),
        r.billing_status ?? '',
        r.assessments_this_month,
        r.monthly_cap,
        `${capPct(r)}%`,
      ]),
    );
    downloadCsv(`writeassess-near-limit-schools-${stamp}.csv`, csv);
  }

  function exportUnderusedSchools() {
    const stamp = todayStamp();
    const csv = buildCsv(
      ['School name', 'Plan', 'Billing status', 'Last assessment'],
      filteredUnderusedSchools.map(s => [
        s.school_name,
        planLabel(s.plan_tier, s.plan_sub_tier),
        s.billing_status ?? '',
        s.last_assessment_at ? isoDate(s.last_assessment_at) : 'Never',
      ]),
    );
    downloadCsv(`writeassess-underused-schools-${stamp}.csv`, csv);
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-6">

        {/* Page header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Platform Analytics</h1>
            <p className="text-muted-foreground text-sm mt-1">Aggregate metrics across all schools. Data refreshes every 5 minutes.</p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="shrink-0">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Export CSV</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={exportSummary} disabled={isLoading}>
                Analytics summary
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportSchools} disabled={isLoading}>
                All schools{hasFilters ? ' (filtered)' : ''}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportNearLimit} disabled={isLoading || filteredCapProximity.length === 0}>
                Near-limit schools ({filteredCapProximity.length})
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportUnderusedSchools} disabled={isLoading || filteredUnderusedSchools.length === 0}>
                Underused schools ({filteredUnderusedSchools.length})
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Filter bar */}
        <Card>
          <CardContent className="py-3">
            <div className="flex flex-wrap gap-2 items-center">
              <Select value={filterPlan} onValueChange={setFilterPlan}>
                <SelectTrigger className="w-36 h-8 text-sm">
                  <SelectValue placeholder="Plan tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All plans</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="school">School</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterBilling} onValueChange={setFilterBilling}>
                <SelectTrigger className="w-40 h-8 text-sm">
                  <SelectValue placeholder="Billing status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All billing</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="trialing">Trialing</SelectItem>
                  <SelectItem value="past_due">Past due</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterStatus} onValueChange={v => setFilterStatus(v as any)}>
                <SelectTrigger className="w-36 h-8 text-sm">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All schools</SelectItem>
                  <SelectItem value="active">Active only</SelectItem>
                  <SelectItem value="suspended">Suspended only</SelectItem>
                </SelectContent>
              </Select>

              <Input
                placeholder="Search school name..."
                value={filterSchool}
                onChange={e => setFilterSchool(e.target.value)}
                className="h-8 w-52 text-sm"
              />

              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground shrink-0">Joined</span>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  className="h-8 w-36 text-sm"
                  title="From date"
                />
                <span className="text-xs text-muted-foreground">–</span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  className="h-8 w-36 text-sm"
                  title="To date"
                />
              </div>

              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 px-2 text-muted-foreground">
                  <X className="h-3.5 w-3.5 mr-1" />
                  Clear
                </Button>
              )}

              {hasFilters && !isLoading && (
                <span className="text-xs text-muted-foreground ml-1">
                  {filteredTotal} of {totalSchools} schools
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Date range stats card */}
        {(dateFrom || dateTo) && (
          <Card>
            <CardContent className="py-3 space-y-2">
              <p className="text-sm font-medium">
                Stats for{' '}
                <span className="text-primary">
                  {dateFrom ? new Date(dateFrom + 'T12:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '…'}
                  {' – '}
                  {dateTo   ? new Date(dateTo   + 'T12:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'today'}
                </span>
              </p>
              {!dateFrom || !dateTo ? (
                <p className="text-xs text-muted-foreground">Set both a start and end date to see range stats.</p>
              ) : rangeLoading ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {Array.from({ length: 4 }).map((_, i) => <StatCardSkeleton key={i} />)}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <StatCard label="New schools"          value={rangeStats?.new_schools    ?? 0} />
                  <StatCard label="New users"            value={rangeStats?.new_users      ?? 0} />
                  <StatCard label="Assessments submitted" value={rangeStats?.assessments    ?? 0} />
                  <StatCard label="Active schools"       value={rangeStats?.active_schools ?? 0} />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Advanced filter builder */}
        <AnalyticsAdvancedFilters
          groups={filterGroups}
          onChange={setFilterGroups}
        />

        {/* ── A: Platform Overview ─────────────────────────────────────────── */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Platform overview
            {filteredOverview && <span className="text-xs font-normal normal-case ml-2 text-primary">(filtered)</span>}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => <StatCardSkeleton key={i} />)
            ) : (
              <>
                <StatCard label="Total schools"     value={filteredOverview?.total_schools     ?? overview?.total_schools     ?? 0} />
                <StatCard label="Active schools"    value={filteredOverview?.active_schools    ?? overview?.active_schools    ?? 0} />
                <StatCard label="Suspended"         value={filteredOverview?.suspended_schools ?? overview?.suspended_schools ?? 0} />
                <StatCard label="New schools"       value={filteredOverview?.new_schools_this_month ?? overview?.new_schools_this_month ?? 0} sub="this month" />
                <StatCard label="Total users"       value={filteredOverview ? '—' : (overview?.total_users ?? 0)} />
                <StatCard label="New users"         value={filteredOverview ? '—' : (overview?.new_users_this_month ?? 0)} sub={filteredOverview ? 'not available per school' : 'this month'} />
                <StatCard label="Total assessments" value={filteredOverview?.total_assessments      ?? overview?.total_assessments      ?? 0} />
                <StatCard label="Assessments"       value={filteredOverview?.assessments_this_month ?? overview?.assessments_this_month ?? 0} sub="this month" />
              </>
            )}
          </div>
        </section>

        {/* ── B: Plan / Billing Distribution ──────────────────────────────── */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Plan &amp; billing distribution
            {hasFilters && <span className="text-xs font-normal normal-case ml-2 text-primary">(filtered)</span>}
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Schools by plan tier</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? <Skeleton className="h-48 w-full" /> : (
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={filteredPlanChartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill="hsl(187, 65%, 42%)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Billing status</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? <Skeleton className="h-48 w-full" /> : (
                  <div className="space-y-2 pt-2">
                    {filteredBillingStatusData.map(row => (
                      <div key={row.billing_status} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <span
                            className="inline-block h-2.5 w-2.5 rounded-full"
                            style={{ background: BILLING_STATUS_COLOURS[row.billing_status] ?? '#94a3b8' }}
                          />
                          <span className="capitalize">{row.billing_status.replace('_', ' ')}</span>
                        </div>
                        <span className="font-medium tabular-nums">
                          {row.cnt} <span className="text-muted-foreground font-normal">({pct(row.cnt, filteredTotal)})</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ── C: Usage Analytics ──────────────────────────────────────────── */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Usage analytics
            {hasFilters && <span className="text-xs font-normal normal-case ml-2 text-primary">(filtered)</span>}
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            {/* Monthly trend — always platform-wide, not filtered */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Assessments per month (last 12 months — platform-wide)</CardTitle>
              </CardHeader>
              <CardContent>
                {trendLoading ? <Skeleton className="h-52 w-full" /> : trend.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">No assessment data yet.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={trend} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month_label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey="assessment_count" name="Assessments" stroke="hsl(187, 65%, 42%)" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="active_school_count" name="Active schools" stroke="hsl(221, 83%, 53%)" strokeWidth={2} dot={false} strokeDasharray="4 2" />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Usage bands */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Schools by assessment volume (all time)</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? <Skeleton className="h-48 w-full" /> : (
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={filteredUsageBands} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="band" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="cnt" name="Schools" fill="hsl(187, 65%, 42%)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Top 10 schools */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Top 10 schools by assessment volume</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {isLoading ? <div className="p-4"><Skeleton className="h-40 w-full" /></div> : filteredTopSchools.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">No matching schools.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8 text-xs">#</TableHead>
                        <TableHead className="text-xs">School</TableHead>
                        <TableHead className="text-xs">Plan</TableHead>
                        <TableHead className="text-right text-xs">Assessments</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTopSchools.map((s, i) => (
                        <TableRow
                          key={s.school_id}
                          className="cursor-pointer"
                          onClick={() => navigate(`/admin/schools/${s.school_id}`)}
                        >
                          <TableCell className="text-muted-foreground text-xs">{i + 1}</TableCell>
                          <TableCell className="font-medium text-sm">{s.school_name}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{planLabel(s.plan_tier, s.plan_sub_tier)}</TableCell>
                          <TableCell className="text-right font-mono text-sm">{s.total_assessments}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Underused schools */}
          {!isLoading && filteredUnderusedSchools.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Underused paid/trial schools
                  <span className="text-muted-foreground font-normal text-sm">(0 assessments in last 30 days)</span>
                  <Badge variant="secondary" className="ml-1">{filteredUnderusedSchools.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">School</TableHead>
                      <TableHead className="text-xs">Plan</TableHead>
                      <TableHead className="text-xs">Billing</TableHead>
                      <TableHead className="text-xs">Last assessment</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUnderusedSchools.map(s => (
                      <TableRow key={s.school_id} className="cursor-pointer" onClick={() => navigate(`/admin/schools/${s.school_id}`)}>
                        <TableCell className="font-medium text-sm">{s.school_name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{planLabel(s.plan_tier, s.plan_sub_tier)}</TableCell>
                        <TableCell>
                          <Badge variant={s.billing_status === 'active' ? 'default' : 'secondary'} className="text-xs capitalize">
                            {s.billing_status ?? '—'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{formatDate(s.last_assessment_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </section>

        {/* ── D: Product Adoption ─────────────────────────────────────────── */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Product adoption</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => <StatCardSkeleton key={i} />)
            ) : (() => {
              const ad = data?.adoption;
              const inv = data?.invite_stats;
              return (
                <>
                  <Card>
                    <CardContent className="pt-5 pb-4">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <p className="text-2xl font-bold">{ad?.schools_with_classes ?? 0}</p>
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">Schools with classes set up</p>
                      <p className="text-xs text-muted-foreground">{pct(ad?.schools_with_classes ?? 0, totalSchools)} of total</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-5 pb-4">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <p className="text-2xl font-bold">{ad?.schools_with_pupils ?? 0}</p>
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">Schools with pupils added</p>
                      <p className="text-xs text-muted-foreground">{pct(ad?.schools_with_pupils ?? 0, totalSchools)} of total</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-5 pb-4">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <p className="text-2xl font-bold">{ad?.schools_with_completed_assessment ?? 0}</p>
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">Schools with a completed assessment</p>
                      <p className="text-xs text-muted-foreground">{pct(ad?.schools_with_completed_assessment ?? 0, totalSchools)} of total</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-5 pb-4">
                      <p className="text-2xl font-bold">{ad?.monthly_active_schools ?? 0}</p>
                      <p className="text-sm text-muted-foreground mt-0.5">Monthly active schools</p>
                      <p className="text-xs text-muted-foreground">had ≥1 assessment this month</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-5 pb-4">
                      <p className="text-2xl font-bold">
                        {inv?.total_invites ? pct(inv.accepted_invites, inv.total_invites) : '—'}
                      </p>
                      <p className="text-sm text-muted-foreground mt-0.5">Teacher invite acceptance</p>
                      <p className="text-xs text-muted-foreground">
                        {inv?.accepted_invites ?? 0} of {inv?.total_invites ?? 0} invites accepted
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-5 pb-4">
                      <p className="text-2xl font-bold">{overview?.avg_assessments_per_active_school ?? '—'}</p>
                      <p className="text-sm text-muted-foreground mt-0.5">Avg assessments / active school</p>
                      <p className="text-xs text-muted-foreground">this month, schools with ≥1</p>
                    </CardContent>
                  </Card>
                </>
              );
            })()}
          </div>
        </section>

        {/* ── E: Assessment Quality ────────────────────────────────────────── */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Assessment quality</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Grade distribution</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? <Skeleton className="h-44 w-full" /> : gradeData.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">No grade data yet.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={gradeData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name} ${Math.round((percent ?? 0) * 100)}%`} labelLine={false}>
                        {gradeData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Assessment status</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? <Skeleton className="h-44 w-full" /> : (
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={data?.status_distribution ?? []} layout="vertical" margin={{ top: 0, right: 8, left: 20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                      <YAxis type="category" dataKey="status" tick={{ fontSize: 12 }} width={70} />
                      <Tooltip />
                      <Bar dataKey="cnt" name="Count" fill="hsl(187, 65%, 42%)" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Average fluency scores</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-2">
                {isLoading ? <Skeleton className="h-44 w-full" /> : (() => {
                  const f = data?.fluency_averages;
                  if (!f || f.fluency_sample_count === 0) {
                    return <p className="text-sm text-muted-foreground py-4 text-center">No fluency data yet.</p>;
                  }
                  const metrics = [
                    { label: 'Syntactic maturity', value: f.avg_syntactic_maturity },
                    { label: 'Textual cohesion',   value: f.avg_textual_cohesion },
                    { label: 'Lexical precision',  value: f.avg_lexical_precision },
                  ];
                  return (
                    <>
                      {metrics.map(m => (
                        <div key={m.label} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{m.label}</span>
                            <span className="font-medium">{m.value ?? '—'}<span className="text-muted-foreground text-xs">/10</span></span>
                          </div>
                          <Progress value={m.value != null ? (m.value / 10) * 100 : 0} className="h-2" />
                        </div>
                      ))}
                      <p className="text-xs text-muted-foreground pt-1">Based on {f.fluency_sample_count.toLocaleString()} assessments</p>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ── F: Operational Health ────────────────────────────────────────── */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Operational health
            {hasFilters && <span className="text-xs font-normal normal-case ml-2 text-primary">(filtered)</span>}
          </h2>

          {!isLoading && filteredCapProximity.length === 0 ? (
            <Card>
              <CardContent className="py-6 flex items-center gap-3 text-sm text-muted-foreground">
                <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
                No schools are currently at or near their monthly assessment cap.
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Schools near monthly assessment cap
                  <span className="text-muted-foreground font-normal text-sm">(≥80% used this month)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {isLoading ? <div className="p-4"><Skeleton className="h-32 w-full" /></div> : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">School</TableHead>
                        <TableHead className="text-xs">Plan</TableHead>
                        <TableHead className="text-xs">Billing</TableHead>
                        <TableHead className="text-xs text-right">Used / Cap</TableHead>
                        <TableHead className="text-xs text-right">%</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCapProximity
                        .slice()
                        .sort((a, b) => capPct(b) - capPct(a))
                        .map(row => {
                          const pctUsed = capPct(row);
                          return (
                            <TableRow key={row.school_id} className="cursor-pointer" onClick={() => navigate(`/admin/schools/${row.school_id}`)}>
                              <TableCell className="font-medium text-sm">{row.school_name}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{planLabel(row.plan_tier, row.plan_sub_tier)}</TableCell>
                              <TableCell>
                                <Badge variant={row.billing_status === 'active' ? 'default' : 'secondary'} className="text-xs capitalize">
                                  {row.billing_status ?? '—'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right font-mono text-sm">
                                {row.assessments_this_month} / {row.monthly_cap}
                              </TableCell>
                              <TableCell className="text-right">
                                <Badge variant={pctUsed >= 100 ? 'destructive' : 'secondary'} className="text-xs">
                                  {pctUsed}%
                                </Badge>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}
        </section>

        {/* ── G: All Schools ───────────────────────────────────────────────── */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            All schools
            {hasFilters && <span className="text-xs font-normal normal-case ml-2 text-primary">(filtered)</span>}
            <span className="text-xs font-normal normal-case ml-2 text-muted-foreground">
              {filteredSchools.length} {filteredSchools.length === 1 ? 'school' : 'schools'}
            </span>
          </h2>
          <Card>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="p-4"><Skeleton className="h-48 w-full" /></div>
              ) : filteredSchools.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground">No schools match the current filters.</p>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">School</TableHead>
                        <TableHead className="text-xs">Plan</TableHead>
                        <TableHead className="text-xs">Billing</TableHead>
                        <TableHead className="text-xs">Joined</TableHead>
                        <TableHead className="text-right text-xs">Total</TableHead>
                        <TableHead className="text-right text-xs">This month</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pagedSchools.map(s => (
                        <TableRow
                          key={s.school_id}
                          className="cursor-pointer"
                          onClick={() => navigate(`/admin/schools/${s.school_id}`)}
                        >
                          <TableCell className="text-sm font-medium max-w-[220px] truncate">{s.school_name}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{planLabel(s.plan_tier, s.plan_sub_tier)}</TableCell>
                          <TableCell className="text-xs">
                            {s.billing_status ? (
                              <Badge variant="outline" className="text-xs capitalize">{s.billing_status.replace('_', ' ')}</Badge>
                            ) : '—'}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{formatDate(s.school_created_at)}</TableCell>
                          <TableCell className="text-right text-xs">{s.total_assessments}</TableCell>
                          <TableCell className="text-right text-xs">{s.assessments_this_month}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-2 border-t text-xs text-muted-foreground">
                      <span>Page {schoolPage + 1} of {totalPages}</span>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="h-7 text-xs" disabled={schoolPage === 0} onClick={() => setSchoolPage(p => p - 1)}>
                          Previous
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 text-xs" disabled={schoolPage >= totalPages - 1} onClick={() => setSchoolPage(p => p + 1)}>
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </section>

      </div>
    </DashboardLayout>
  );
}
